import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import useScrollToTop from '../hooks/useScrollToTop';

const Home: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#1976d2', // Fallback color if image is not loaded
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('home.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('home.hero.subtitle')}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              component={Link}
              to="/contact"
              sx={{ mt: 4 }}
            >
              {t('home.hero.cta')}
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* Services Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('home.services.title')}
        </Typography>
        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={4}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {t('home.services.us.title')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('home.services.us.description')}
                  </Typography>
                  <Button
                    component={Link}
                    to="/us-green-card"
                    variant="outlined"
                    color="primary"
                  >
                    {t('home.services.learnMore')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={4}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {t('home.services.citizenship.title')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('home.services.citizenship.description')}
                  </Typography>
                  <Button
                    component={Link}
                    to="/citizenship"
                    variant="outlined"
                    color="primary"
                  >
                    {t('home.services.learnMore')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={4}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {t('home.services.residency.title')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('home.services.residency.description')}
                  </Typography>
                  <Button
                    component={Link}
                    to="/other-citizenship"
                    variant="outlined"
                    color="primary"
                  >
                    {t('home.services.learnMore')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={4}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {t('home.services.assetManagement.title')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('home.services.assetManagement.description')}
                  </Typography>
                  <Button
                    component={Link}
                    to="/other-programs"
                    variant="outlined"
                    color="primary"
                  >
                    {t('home.services.learnMore')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={4}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {t('home.services.corporateOverseas.title')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('home.services.corporateOverseas.description')}
                  </Typography>
                  <Button
                    component={Link}
                    to="/other-programs"
                    variant="outlined"
                    color="primary"
                  >
                    {t('home.services.learnMore')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={4}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {t('home.services.educationPlanning.title')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('home.services.educationPlanning.description')}
                  </Typography>
                  <Button
                    component={Link}
                    to="/other-programs"
                    variant="outlined"
                    color="primary"
                  >
                    {t('home.services.learnMore')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* About Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Typography variant="h3" component="h2" gutterBottom>
                  {t('home.about.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('home.about.description')}
                </Typography>
                <Button
                  component={Link}
                  to="/about"
                  variant="contained"
                  color="primary"
                >
                  {t('home.about.learnMore')}
                </Button>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 